import { motion, useScroll, useTransform } from 'motion/react'
import { useRef } from 'react'
import { siteContent } from '#app/utils/site/content'
import {
	HERO_CLOUD_SEEDS,
	type HeroCloudDrift,
	heroCloudOpacity,
	heroCloudTransform,
	heroCopyOpacity,
	heroCopyTransform,
	heroCopyVisibility,
	heroPhotoTransform,
	heroVeilOpacity,
} from '#app/utils/site/hero-motion'
import { useReducedMotionSafe } from '#app/utils/site/scene-clock'
import {
	type Audience,
	HERO_IMAGE,
	SITE_LINKS,
	ctaHrefFor,
} from '#app/utils/site/site-links'
import { useOptionalUser } from '#app/utils/user'
import { BigHeading, Pill, TextLink } from '../primitives'

const CLOUD_GROUPS: readonly HeroCloudDrift[] = ['left', 'right']

/**
 * The scroll-pinned photo hero. A 150vh section pins a full-viewport stage
 * for the first 50vh of scrolling: the copy lifts and fades, the white veil
 * clears, the Toronto photo eases back and twelve blurred clouds rise while
 * the timeline track slides up over the tail through a white fog.
 *
 * Every animated value is a `useTransform` of the section's scroll progress
 * (pure maths in hero-motion.ts), so nothing re-renders per frame and no
 * layout is read while scrolling. Reduced motion drops the pin, the
 * transforms and the clouds (layout in hero.css).
 */
export function PhotoHero({ audience }: { audience: Audience }) {
	const copy = siteContent.hero[audience]
	const ctaHref = ctaHrefFor(copy.ctaHref, Boolean(useOptionalUser()))
	const sectionRef = useRef<HTMLElement>(null)
	const reduced = useReducedMotionSafe()
	const { scrollYProgress } = useScroll({
		target: sectionRef,
		offset: ['start start', 'end end'],
	})
	const photoTransform = useTransform(scrollYProgress, heroPhotoTransform)
	const veilOpacity = useTransform(scrollYProgress, heroVeilOpacity)
	const copyOpacity = useTransform(scrollYProgress, heroCopyOpacity)
	const copyTransform = useTransform(scrollYProgress, heroCopyTransform)
	const copyVisibility = useTransform(scrollYProgress, heroCopyVisibility)
	const cloudOpacity = useTransform(scrollYProgress, heroCloudOpacity)
	const cloudLeft = useTransform(scrollYProgress, (p) =>
		heroCloudTransform(p, 'left'),
	)
	const cloudRight = useTransform(scrollYProgress, (p) =>
		heroCloudTransform(p, 'right'),
	)
	const cloudTransforms = { left: cloudLeft, right: cloudRight }

	return (
		<section
			ref={sectionRef}
			id="tt-hero"
			className="tt-hero"
			aria-labelledby="tt-hero-heading"
		>
			<div className="tt-hero-stage">
				<motion.div
					className="tt-hero-layer tt-hero-photo"
					style={reduced ? undefined : { transform: photoTransform }}
				>
					<img
						src={HERO_IMAGE.src}
						srcSet={HERO_IMAGE.srcSet}
						sizes={HERO_IMAGE.sizes}
						alt={HERO_IMAGE.alt}
						fetchPriority="high"
						decoding="async"
					/>
				</motion.div>
				<motion.div
					aria-hidden="true"
					className="tt-hero-layer tt-hero-veil"
					style={reduced ? undefined : { opacity: veilOpacity }}
				/>
				{reduced ? null : (
					<div aria-hidden="true" className="tt-hero-layer tt-hero-clouds">
						{CLOUD_GROUPS.map((drift) => (
							<motion.div
								key={drift}
								className="tt-hero-cloud-group"
								style={{
									transform: cloudTransforms[drift],
									opacity: cloudOpacity,
								}}
							>
								{HERO_CLOUD_SEEDS.filter((seed) => seed.drift === drift).map(
									(seed) => (
										<div
											key={seed.left}
											className="tt-hero-cloud"
											style={{
												left: `${seed.left}%`,
												top: `${seed.top}%`,
												width: `${seed.width}vw`,
												height: `${seed.height}vw`,
											}}
										/>
									),
								)}
							</motion.div>
						))}
					</div>
				)}
				<motion.div
					className="tt-hero-layer tt-hero-copy"
					style={
						reduced
							? undefined
							: {
									opacity: copyOpacity,
									transform: copyTransform,
									visibility: copyVisibility,
								}
					}
				>
					<div
						key={audience}
						className="tt-swap-in flex w-full flex-col items-center"
					>
						<BigHeading as="h1" id="tt-hero-heading" className="tt-hero-title">
							{copy.lines.map((line) => (
								<span key={line} className="block">
									{line}
								</span>
							))}
						</BigHeading>
						<p className="tt-hero-sub mt-[22px] text-[clamp(17px,1.5vw,20px)] leading-[1.5] text-tt-body">
							{copy.sub}
						</p>
						<div className="tt-hero-ctas flex flex-wrap items-center justify-center">
							<Pill href={ctaHref} arrow>
								{copy.cta}
							</Pill>
							<TextLink href={SITE_LINKS.howItWorks}>{copy.secondary}</TextLink>
						</div>
					</div>
				</motion.div>
			</div>
			<div aria-hidden="true" className="tt-hero-fade" />
		</section>
	)
}
